import React from "react";
import { Button } from "../common";
import { Download, Linkedin, Share, GraduationCap } from "lucide-react";

interface CertificateViewProps {
  courseTitle: string;
  userName: string;
  score: number;
  courseId: string;
  lessonId: string;
  certificateRef: React.RefObject<HTMLDivElement | null>;
  isGeneratingCertificate: boolean;
  onDownloadCertificate: () => void;
  onShareOnLinkedIn: () => void;
  onShareCertificate: () => void;
  onBack: () => void;
}

const CertificateView: React.FC<CertificateViewProps> = ({
  courseTitle,
  userName,
  score,
  courseId,
  lessonId,
  certificateRef,
  isGeneratingCertificate,
  onDownloadCertificate,
  onShareOnLinkedIn,
  onShareCertificate,
  onBack,
}) => {
  return (
    <div className="w-full flex-grow flex flex-col">
      {/* Enhanced background with subtle pattern and adjusted padding */}
      <div 
        className="flex flex-col items-center justify-center flex-grow bg-gradient-to-br from-amber-200 via-amber-100 to-amber-50 rounded-3xl p-3 shadow-2xl relative overflow-hidden mx-auto w-full"
        style={{
          backgroundImage: `
            radial-gradient(circle at top right, rgba(252, 211, 77, 0.1) 0%, transparent 50%),
            radial-gradient(circle at bottom left, rgba(252, 211, 77, 0.1) 0%, transparent 50%),
            linear-gradient(to bottom right, #fef3c7, #fffbeb)
          `
        }}
      >
        {/* Animated background elements (kept subtle) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-amber-300 rounded-full opacity-10 blur-2xl animate-pulse"></div>
          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-amber-300 rounded-full opacity-10 blur-2xl animate-pulse"></div>
        </div>
        
        {/* Wrapper for scrollable content */}
        <div className="w-full flex flex-col items-center flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-amber-400 scrollbar-track-amber-200 p-2 relative z-10">
          {/* Content Header */}
          <div className="text-center mb-3 relative z-10 flex-shrink-0">
            <div className="inline-block p-2 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full mb-2 shadow-lg ring-2 ring-white/50">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-semibold text-amber-800 mb-1 tracking-tight">Your Certificate</h1>
            <p className="text-base text-amber-700/90 max-w-md mx-auto font-medium">Download and share your achievement!</p>
          </div>
          
          {/* Certificate Preview - Enhanced design */}
          <div className="w-full max-w-2xl mb-3 relative z-10 flex-shrink-0">
            <div 
              ref={certificateRef}
              className="bg-white p-5 rounded-lg shadow-lg border-2 border-amber-400/50 overflow-hidden transform hover:scale-[1.02] transition-transform duration-300 relative"
            >
              {/* Decorative Border */}
              <div className="absolute inset-0 border-4 border-amber-300/60 rounded-md pointer-events-none m-1"></div>

              <div className="flex flex-col items-center relative z-10">
                <div className="mb-2">
                  <GraduationCap className="w-16 h-16 text-amber-500" />
                </div>
                {/* Refined Typography */}
                <h2 className="text-2xl font-bold text-amber-800 mb-1 text-center tracking-tight">Certificate of Completion</h2>
                {/* Decorative Line */}
                <div className="w-28 h-0.5 bg-gradient-to-r from-amber-400 to-amber-500 mb-3"></div>
                
                <p className="text-sm text-gray-600 mb-1 text-center">This is to certify that</p>
                <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">{userName}</h3>
                <p className="text-sm text-gray-600 mb-1 text-center">has successfully completed the course</p>
                <h4 className="text-xl font-semibold text-amber-700 mb-4 text-center">{courseTitle}</h4>
                
                {/* Details Section */}
                <div className="flex justify-around w-full text-gray-600 text-xs border-t border-amber-200/80 pt-2 mt-2">
                  <div className="text-center px-2">
                    <p className="font-semibold text-amber-700/90 uppercase text-[10px] tracking-wider">Date</p>
                    <p className="font-medium">{new Date().toLocaleDateString()}</p>
                  </div>
                  <div className="text-center border-x border-amber-200/80 px-2">
                    <p className="font-semibold text-amber-700/90 uppercase text-[10px] tracking-wider">Score</p>
                    <p className="font-medium">{score}%</p>
                  </div>
                  <div className="text-center px-2">
                    <p className="font-semibold text-amber-700/90 uppercase text-[10px] tracking-wider">Certificate ID</p>
                    <p className="font-medium text-[11px]">{courseId?.substring(0, 8)}-{lessonId?.substring(0, 8)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-2 relative z-10 mb-2 flex-shrink-0">
            <Button 
              variant="filled" 
              onClick={onDownloadCertificate}
              disabled={isGeneratingCertificate}
              className="px-5 py-1.5 text-sm font-medium bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 border-amber-800/50 border transform hover:scale-105 transition-all duration-300 shadow-lg rounded-full flex items-center gap-1.5 text-white"
            >
              <Download className="w-3.5 h-3.5" />
              {isGeneratingCertificate ? "Generating..." : "Download"}
            </Button>
            <span className="relative group">
              <Button
                variant="filled"
                disabled
                className="px-5 py-1.5 text-sm font-medium bg-gray-300 text-gray-400 cursor-not-allowed border-gray-300 border rounded-full flex items-center gap-1.5"
              >
                <Linkedin className="w-3.5 h-3.5" />
                LinkedIn
              </Button>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Digital Badge sharing coming soon
              </span>
            </span>
            <Button 
              variant="filled" 
              onClick={onShareCertificate}
              className="px-5 py-1.5 text-sm font-medium bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 border-green-800/50 border transform hover:scale-105 transition-all duration-300 shadow-lg rounded-full flex items-center gap-1.5 text-white"
            >
              <Share className="w-3.5 h-3.5" />
              Share
            </Button>
          </div>
          
          {/* Back Button */}
          <div className="mt-auto pt-2 flex-shrink-0">
            <Button 
              variant="outline"
              onClick={onBack}
              className="px-4 py-1 text-xs font-medium text-amber-700 border-amber-400/80 hover:bg-amber-50/50 transition-all duration-300 rounded-full"
            >
              Back to Course
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateView; 